import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import style from "./bms-config.scss";
import {
    BBTIConfiguration,
    DownloadServerConfiguration,
    IdentityVerificationConfiguration,
    InsuranceConfiguration,
    LeaseConfiguration,
    NtfConfiguration,
    ValidationConfiguration,
} from "./BmsCommonInterfaces";
import {
    FeatureTestsConfiguration,
    getAppImageAndColours,
    getAppStoreUrl,
    getAvailableApps,
    getComplementaryColor,
    getPlatformTypes,
    MAX_APP_NAME_LENGTH,
    updateFeatureTests,
} from "./bmsUtils";
import Checkbox from "./form-components/BmsCheckboxComponent";
import RadioButton from "./form-components/BmsRadioButtonComponent";
import BmsSectionHeader from "./form-components/BmsSectionHeader";
import InputComponent from "./form-components/BmsTextInputComponent";
import defaultConfiguration from "./UiConfiguration.json";
import Warning from "components/icons/Warning";
import {
    BMS_LICENSE_FEATURE_ALL,
    BMS_LICENSE_FEATURE_GATHER_IMEI,
    BMS_LICENSE_FEATURE_LEASE,
} from "components/licenses/common";
import BmsOptionsInputComponent from "components/licenses/license-configuration/bms/form-components/BmsOptionsInputComponent";
import Modal from "components/modal/Modal";
import { StoreState } from "store";

interface FormValues {
    brand: {
        appName?: string;
        image?: string;
        tag_line?: string;
        primaryColor?: string;
        secondaryColor?: string;
        tertiaryColor?: string;
        checkContrast?: boolean;
    };
    platform: string;
    selectedApp: string;
    marketing_name_url: string;
    features?: string[];
    limit_check?: {
        failure_limit?: number;
        error_limit?: number;
        validity_period?: number;
    };
    componentFeatures: {
        [key: string]: {
            enabled: boolean;
            components: { name: string; enabled: boolean }[];
        };
    };
    bbtiConfiguration: BBTIConfiguration;
    validationConfiguration: ValidationConfiguration;
    insuranceConfiguration: InsuranceConfiguration;
    ntfConfiguration: NtfConfiguration;
    leaseConfiguration: LeaseConfiguration;
    identityVerificationConfiguration: IdentityVerificationConfiguration;
    downloadAppPageConfiguration: DownloadServerConfiguration;
}

interface Props {
    formValues: FormValues;
    setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
    featuresWithAvailableLicenses?: string[];
    showAdvanced: boolean;
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});
const connector = connect(mapState);

const GenericConfigurationForm = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const platformType = getPlatformTypes();
    const availableApps = getAvailableApps();
    const singleComponentSelectionFeatures = [BMS_LICENSE_FEATURE_GATHER_IMEI, BMS_LICENSE_FEATURE_LEASE];
    const COLOR_FIELDS = ["primaryColor", "secondaryColor", "tertiaryColor"];
    const enableFeatures = (
        defaultConfiguration: {
            [key: string]: {
                enabled: boolean;
                components: { name: string; enabled: boolean }[];
            };
        },
        features: string[] | undefined
    ) => {
        if (!features) {
            return defaultConfiguration;
        }

        const updatedConfiguration = { ...defaultConfiguration };
        features.forEach((feature) => {
            if (feature in updatedConfiguration) {
                updatedConfiguration[feature].enabled = true;
            }
        });
        return updatedConfiguration;
    };

    const [showPlatformChangeWarning, setShowPlatformChangeWarning] = React.useState(false);

    React.useEffect(() => {
        if (Object.keys(props.formValues.componentFeatures).length === 0) {
            props.setFormValues({
                ...props.formValues,
                componentFeatures: enableFeatures(defaultConfiguration.component_feature, props.formValues.features),
            });
        }
    }, [props.formValues.componentFeatures, props.setFormValues]);

    const handleInputChange = (fieldName: string, value: string) => {
        const updates = {
            ...props.formValues,
            brand: {
                ...props.formValues.brand,
                [fieldName]: fieldName === "checkContrast" ? value === "true" : value,
            },
        };

        if (fieldName === "appName") {
            updates.downloadAppPageConfiguration = {
                ...props.formValues.downloadAppPageConfiguration,
                title: value,
            };
        }

        // Sync color changes to downloadAppPageConfiguration
        if (COLOR_FIELDS.includes(fieldName)) {
            updates.downloadAppPageConfiguration = {
                ...props.formValues.downloadAppPageConfiguration,
                [fieldName]: value,
            };
        }

        props.setFormValues(updates);
    };

    const handleComponentChange = (featureKey: string, componentName: string | null, isChecked: boolean) => {
        props.setFormValues((currentFormValues) => {
            const updatedFormValues = { ...currentFormValues };
            const feature = updatedFormValues.componentFeatures[featureKey];

            if (feature) {
                if (featureKey === "FaultCheck" && isChecked) {
                    // If FaultCheck is enabled, also enable Recommendations
                    const recommendationsFeature = updatedFormValues.componentFeatures["Recommendations"];
                    if (recommendationsFeature) {
                        recommendationsFeature.enabled = true;
                    }
                }

                if (singleComponentSelectionFeatures.includes(featureKey) && componentName != null) {
                    feature.components.forEach((component) => {
                        if (component.name.toLowerCase() != componentName.toLowerCase()) {
                            component.enabled = false;
                        }
                    });
                }

                if (componentName === null) {
                    // If the feature checkbox is unchecked, uncheck all components
                    feature.enabled = isChecked;
                    if (!isChecked) {
                        feature.components.forEach((component) => {
                            component.enabled = false;
                        });
                    }
                } else {
                    const components = feature.components;

                    if (components) {
                        const component = components.find((c) => c.name === componentName);

                        if (component) {
                            component.enabled = isChecked;
                        }
                    }
                }
            }

            return updatedFormValues;
        });
    };

    function disableAllComponentFeatures() {
        props.setFormValues((currentFormValues) => {
            const updatedFormValues = { ...currentFormValues };
            const features = updatedFormValues.componentFeatures;

            if (features) {
                for (const featureKey in features) {
                    features[featureKey].enabled = false;
                    features[featureKey].components = features[featureKey].components.map((component) => ({
                        ...component,
                        enabled: false,
                    }));
                }
            }

            return updatedFormValues;
        });
    }

    const handleAppSelection = (selectedApp: string) => {
        props.setFormValues((currentFormValues) => {
            const appLinks = getAppStoreUrl(selectedApp);
            const updates = {
                ...currentFormValues,
                selectedApp: selectedApp,
                downloadAppPageConfiguration: {
                    ...currentFormValues.downloadAppPageConfiguration,
                    androidFirebaseLink: appLinks.androidLink,
                    iOSFirebaseLink: appLinks.iosLink,
                },
            };

            // Update splash screen image based on app selection
            const currentDomain = window.location.origin;
            const currentImage = currentFormValues.brand.image || "";

            if (currentImage) {
                const appImages = getAppImageAndColours();
                const appImage = appImages.find((img) => img.value === selectedApp);
                if (appImage) {
                    updates.brand = {
                        ...currentFormValues.brand,
                        image: `${currentDomain}${appImage.path || ""}`,
                        primaryColor: appImage.primaryColor,
                        secondaryColor: appImage.secondaryColor,
                        tertiaryColor: appImage.tertiaryColor,
                        checkContrast: appImage.checkContrast,
                    };
                    updates.downloadAppPageConfiguration = {
                        ...updates.downloadAppPageConfiguration,
                        primaryColor: appImage.primaryColor,
                        secondaryColor: appImage.secondaryColor,
                        tertiaryColor: appImage.tertiaryColor,
                    };
                }
            }

            return updates;
        });
    };

    const handlePlatformChange = (newValue: string) => {
        const updates = {
            ...props.formValues,
            platform: newValue,
        };

        if (newValue === "browser") {
            const browserUrls = getAppStoreUrl("browser");
            updates.downloadAppPageConfiguration = {
                ...props.formValues.downloadAppPageConfiguration,
                androidFirebaseLink: browserUrls.androidLink,
                iOSFirebaseLink: browserUrls.iosLink,
                autoForward: browserUrls.autoForward,
            };
        }

        props.setFormValues(updates);

        if (newValue === "sdk") {
            disableAllComponentFeatures();
            return;
        }

        updateFeatureTests(props.formValues as FeatureTestsConfiguration, newValue);
        setShowPlatformChangeWarning(true);
    };

    const hidePlatformChangeWarning = () => {
        setShowPlatformChangeWarning(false);
    };

    const handleMarketingNameUrlChange = (newValue: string) => {
        props.setFormValues({
            ...props.formValues,
            marketing_name_url: newValue,
        });
    };

    const handleImageLimitChange = (fieldName: string, value: string) => {
        props.setFormValues({
            ...props.formValues,
            limit_check: {
                ...props.formValues.limit_check,
                [fieldName]: Number(value) > 0 ? Number(value) : null,
            },
        });
    };

    const isDisabled = (feature: string) => {
        if (props.featuresWithAvailableLicenses) {
            if (props.featuresWithAvailableLicenses.includes(BMS_LICENSE_FEATURE_ALL)) {
                return false;
            } else {
                return !props.featuresWithAvailableLicenses.includes(feature);
            }
        }
        return true;
    };

    return (
        <div>
            <div className={classNames(style.boxedSection)}>
                <BmsSectionHeader
                    labelKey={t("Configuration.genericConfigurationForm.headers.brand")}
                    toolTipContent={t("Configuration.tooltips.genericConfigurationForm.brand")}
                />

                <div className={classNames(style.formSection)}>
                    <InputComponent
                        labelKey={t("Configuration.genericConfigurationForm.appName")}
                        toolTipContent={t("Configuration.tooltips.genericConfigurationForm.appName", {
                            max_length: MAX_APP_NAME_LENGTH,
                        })}
                        inputId={"appName"}
                        value={props.formValues.brand.appName || ""}
                        onChange={(newValue) => handleInputChange("appName", newValue)}
                        maxLength={MAX_APP_NAME_LENGTH}
                    />

                    <InputComponent
                        labelKey={t("Configuration.genericConfigurationForm.tagLine")}
                        toolTipContent={t("Configuration.tooltips.genericConfigurationForm.tagLine")}
                        inputId={"tag_line"}
                        value={props.formValues.brand.tag_line || ""}
                        onChange={(newValue) => handleInputChange("tag_line", newValue)}
                    />

                    <span className={classNames(style.inputContainer)}>
                        <label className={classNames(style.bmsLabel)}>
                            {t("Configuration.genericConfigurationForm.primaryColor")}
                        </label>
                        <div style={{ display: "flex", alignItems: "center", width: "31%" }}>
                            <input
                                type="color"
                                value={props.formValues.brand.primaryColor || "#000000"}
                                onChange={(e) => handleInputChange("primaryColor", e.target.value)}
                                className={classNames(style.colorPickerIcon)}
                            />
                            <input
                                type="text"
                                value={props.formValues.brand.primaryColor || ""}
                                onChange={(e) => handleInputChange("primaryColor", e.target.value)}
                                className={classNames(style.brandInput)}
                                style={{
                                    backgroundColor: props.formValues.brand.primaryColor || "",
                                    color: getComplementaryColor(props.formValues.brand.primaryColor || ""),
                                    width: "calc(100% - 32px)",
                                    marginLeft: "0",
                                }}
                            />
                        </div>
                    </span>

                    <span className={classNames(style.inputContainer)}>
                        <label className={classNames(style.bmsLabel)}>
                            {t("Configuration.genericConfigurationForm.secondaryColor")}
                        </label>
                        <div style={{ display: "flex", alignItems: "center", width: "31%" }}>
                            <input
                                type="color"
                                value={props.formValues.brand.secondaryColor || "#000000"}
                                onChange={(e) => handleInputChange("secondaryColor", e.target.value)}
                                className={classNames(style.colorPickerIcon)}
                            />
                            <input
                                type="text"
                                value={props.formValues.brand.secondaryColor || ""}
                                onChange={(e) => handleInputChange("secondaryColor", e.target.value)}
                                className={classNames(style.brandInput)}
                                style={{
                                    backgroundColor: props.formValues.brand.secondaryColor || "",
                                    color: getComplementaryColor(props.formValues.brand.secondaryColor || ""),
                                    width: "calc(100% - 32px)",
                                    marginLeft: "0",
                                }}
                            />
                        </div>
                    </span>
                    <span className={classNames(style.inputContainer)}>
                        <label className={classNames(style.bmsLabel)}>
                            {t("Configuration.genericConfigurationForm.tertiaryColor")}
                        </label>
                        <div style={{ display: "flex", alignItems: "center", width: "31%" }}>
                            <input
                                type="color"
                                value={props.formValues.brand.tertiaryColor || "#000000"}
                                onChange={(e) => handleInputChange("tertiaryColor", e.target.value)}
                                className={classNames(style.colorPickerIcon)}
                            />
                            <input
                                type="text"
                                value={props.formValues.brand.tertiaryColor || ""}
                                onChange={(e) => handleInputChange("tertiaryColor", e.target.value)}
                                className={classNames(style.brandInput)}
                                style={{
                                    backgroundColor: props.formValues.brand.tertiaryColor || "",
                                    color: getComplementaryColor(props.formValues.brand.tertiaryColor || ""),
                                    width: "calc(100% - 32px)",
                                    marginLeft: "0",
                                }}
                            />
                        </div>
                    </span>
                    <Checkbox
                        labelKey={t("Configuration.genericConfigurationForm.checkContrast")}
                        toolTipContent={t("Configuration.tooltips.genericConfigurationForm.checkContrast")}
                        checkboxId={"checkContrast"}
                        checked={props.formValues.brand.checkContrast || false}
                        onChange={(newValue: boolean) => handleInputChange("checkContrast", newValue + "")}
                    />

                    <InputComponent
                        labelKey={t("Configuration.genericConfigurationForm.splashImage")}
                        toolTipContent={t("Configuration.tooltips.genericConfigurationForm.splashImage")}
                        inputId={"image"}
                        value={props.formValues.brand.image || ""}
                        onChange={(newValue) => handleInputChange("image", newValue)}
                    />

                    {props.formValues.brand.image && (
                        <img
                            src={props.formValues.brand.image}
                            alt={t("AltText.logo")}
                            className={classNames(style.displayImage)}
                        />
                    )}
                </div>
            </div>

            <div className={classNames(style.boxedSection)}>
                <BmsSectionHeader
                    labelKey={t("Configuration.genericConfigurationForm.headers.appConfiguration")}
                    toolTipContent={t("Configuration.tooltips.genericConfigurationForm.appConfiguration")}
                />
                <div className={classNames(style.formSection)}>
                    {props.showAdvanced && (
                        <InputComponent
                            labelKey={t("Configuration.genericConfigurationForm.marketingNameUrl")}
                            toolTipContent={t("Configuration.tooltips.genericConfigurationForm.marketingNameUrl")}
                            inputId={"marketing_name_url"}
                            value={props.formValues.marketing_name_url || ""}
                            onChange={(newValue) => handleMarketingNameUrlChange(newValue)}
                        />
                    )}

                    <BmsOptionsInputComponent
                        labelKey={t("Configuration.genericConfigurationForm.platformType")}
                        value={props.formValues.platform}
                        toolTipContent={t("Configuration.tooltips.genericConfigurationForm.platformType")}
                        options={platformType}
                        onChange={handlePlatformChange}
                    />

                    {(props.formValues.platform === "appUSDK" || props.formValues.platform === "appISDK") && (
                        <BmsOptionsInputComponent
                            labelKey={t("Configuration.genericConfigurationForm.appSelection")}
                            value={props.formValues.selectedApp}
                            toolTipContent={t("Configuration.tooltips.genericConfigurationForm.appSelection")}
                            options={availableApps}
                            onChange={handleAppSelection}
                        />
                    )}
                </div>
                {props.showAdvanced && (
                    <div className={classNames(style.boxedSection)}>
                        <BmsSectionHeader
                            labelKey={t("Configuration.genericConfigurationForm.headers.screenFraudPrevention")}
                            toolTipContent={t("Configuration.tooltips.genericConfigurationForm.screenFraudPrevention")}
                        />

                        <InputComponent
                            labelKey={t("Configuration.genericConfigurationForm.failureLimit")}
                            toolTipContent={t("Configuration.tooltips.genericConfigurationForm.failureLimit")}
                            inputId={"failureLimit"}
                            inputType={"number"}
                            value={props.formValues.limit_check?.failure_limit || ""}
                            onChange={(newValue) => handleImageLimitChange("failure_limit", newValue)}
                        />

                        <InputComponent
                            labelKey={t("Configuration.genericConfigurationForm.errorLimit")}
                            toolTipContent={t("Configuration.tooltips.genericConfigurationForm.errorLimit")}
                            inputId={"errorLimit"}
                            inputType={"number"}
                            value={props.formValues.limit_check?.error_limit || ""}
                            onChange={(newValue) => handleImageLimitChange("error_limit", newValue)}
                        />

                        <InputComponent
                            labelKey={t("Configuration.genericConfigurationForm.validityPeriod")}
                            toolTipContent={t("Configuration.tooltips.genericConfigurationForm.validityPeriod")}
                            inputId={"validityPeriod"}
                            inputType={"number"}
                            value={props.formValues.limit_check?.validity_period || undefined}
                            onChange={(newValue) => handleImageLimitChange("validity_period", newValue)}
                        />
                    </div>
                )}
            </div>
            <div className={classNames(style.boxedSection)}>
                <BmsSectionHeader
                    labelKey={t("Configuration.genericConfigurationForm.headers.features")}
                    toolTipContent={t("Configuration.tooltips.genericConfigurationForm.featureSection")}
                />
                {Object.entries(props.formValues.componentFeatures).map(([featureKey, featureValue], index) => (
                    <div className={classNames(style.formSection, style.features)} key={featureKey}>
                        <>
                            <Checkbox
                                labelKey={t(
                                    `Configuration.genericConfigurationForm.features.${featureKey.toLowerCase()}`
                                )}
                                toolTipContent={t(
                                    `Configuration.tooltips.genericConfigurationForm.features.${featureKey.toLowerCase()}`
                                )}
                                checkboxId={`feature-checkbox-${index}`}
                                checked={featureValue.enabled}
                                disabled={isDisabled(featureKey) || props.formValues.platform.toLowerCase() == "sdk"}
                                name={featureKey}
                                onChange={(newValue: boolean) => handleComponentChange(featureKey, null, newValue)}
                            />
                            {featureValue.enabled && (
                                <>
                                    {featureValue.components.map((component, componentIndex) => (
                                        <div
                                            id={"feature"}
                                            className={classNames(style.components)}
                                            key={`${featureKey}-${componentIndex}`}
                                        >
                                            {singleComponentSelectionFeatures.includes(featureKey) ? (
                                                <RadioButton
                                                    labelKey={t(
                                                        `Configuration.genericConfigurationForm.features.components.${component.name.toLowerCase()}`
                                                    )}
                                                    toolTipContent={t(
                                                        `Configuration.tooltips.genericConfigurationForm.features.components.${component.name.toLowerCase()}`
                                                    )}
                                                    name={component.name}
                                                    radioId={`component-radio-${index}-${componentIndex}-${component.name}`}
                                                    checked={component.enabled}
                                                    onChange={(newValue: boolean) =>
                                                        handleComponentChange(featureKey, component.name, newValue)
                                                    }
                                                />
                                            ) : (
                                                <Checkbox
                                                    labelKey={t(
                                                        `Configuration.genericConfigurationForm.features.components.${component.name.toLowerCase()}`
                                                    )}
                                                    toolTipContent={t(
                                                        `Configuration.tooltips.genericConfigurationForm.features.components.${component.name.toLowerCase()}`
                                                    )}
                                                    checkboxId={`component-checkbox-${index}-${componentIndex}`}
                                                    checked={component.enabled}
                                                    name={component.name}
                                                    onChange={(newValue: boolean) =>
                                                        handleComponentChange(featureKey, component.name, newValue)
                                                    }
                                                />
                                            )}
                                        </div>
                                    ))}
                                </>
                            )}
                        </>
                    </div>
                ))}
            </div>

            <Modal
                isOpen={showPlatformChangeWarning}
                hideModal={hidePlatformChangeWarning}
                modalTitle={t("Configuration.genericConfigurationForm.headers.platformChangeModal")}
            >
                <div>
                    <Warning color={props.theme.warningBackgroundColor} strokeColor={"#FFF"} width={20} height={20} />
                    <span className={classNames(style.components)}>
                        {t("Configuration.genericConfigurationForm.platformChangeWarning")}
                    </span>
                </div>
            </Modal>
        </div>
    );
};

export default connector(GenericConfigurationForm);
